import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IFilterLabelValue } from '@kurtosys/types/api/common/IFilterLabelValue.js';
import { IFilterPropertyValue } from '@kurtosys/types/app/filters/IFilterPropertyValue.js';
import { IFilterPropertyValueSelection } from '@kurtosys/types/app/filters/IFilterPropertyValueSelection.js';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { TStoreContext } from '../../../models/index.js';
import { IFilterMenu } from '../../../models/input/IFilterMenu.js';
import { IFilterMenuOption } from '../../../models/input/IFilterMenuOption.js';
import { IMenuValue } from '../../../models/input/IMenuValue.js';
import { IMenuBaseFeatures } from '../../../models/input/menu/features/IMenuBaseFeatures.js';
import { TButtonIconProps } from '../../../models/input/TButtonIconProps.js';
import { TMenuValue } from '../../../models/input/TMenuValue.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { FilterPropertyStore } from '../../App/stores/FilterPropertyStore.js';
import { GlobalInputsStore } from '../../App/stores/GlobalInputsStore.js';
import { InputStoreBase } from '../../shared/InputStoreBase.js';
import { IMegaMenuFilterProps } from '../models/IMegaMenuFilterProps.js';
import { IMegaMenuOptions } from '../models/IMegaMenuOptions.js';
import { IMegaMenuComponentProps } from '../models/index.js';
type IPillProps = components.base.Pill.models.IPillProps;
type IButtonProps = components.base.Button.models.IButtonProps;

export class MegaMenuStore extends InputStoreBase<
	IMegaMenuOptions,
	IMegaMenuComponentProps,
	Map<string, Map<string, IMenuValue>>
> {
	static componentKey = 'megaMenu' as const;

	// Cache the filterMapping values. The input key will be used as the key of the map
	@observable.ref filterMappings: Map<string, IFilterMenu> = new Map();

	// Stores the original item count, for the total values
	filterItemCount: Map<string, number> = new Map();

	// unique instance id for mega menu implementation
	guid: string;

	constructor(
		storeContext: TStoreContext,
		manifest: IManifest,
		inputKey: string,
		options: IMegaMenuOptions,
		componentProps: IMegaMenuComponentProps,
	) {
		super(storeContext, manifest, inputKey, options, componentProps);
		this.guid = common.commonUtils.generateGuid();
		this.value = new Map();
		makeObservable(this);
		this.registerFilterCallbacks();
	}

	registerFilterCallbacks = () => {
		for (const filter of this.options.filters) {
			if (this.canDisplayMenuItemCount(filter)) {
				this.filterPropertyStore.registerCallback(this.updateValue);
			}
		}
	};

	@computed
	get filterPropertyStore(): FilterPropertyStore {
		return this.storeContext.get<FilterPropertyStore>('filterPropertyStore');
	}

	get features(): IMenuBaseFeatures {
		return this.options.features || {};
	}

	@computed
	get hasData(): boolean {
		if (this.options?.noDataOptions !== undefined) {
			let hasData = false;
			for (const value of this.filterMappings.values()) {
				if (value.options?.length > 0) {
					hasData = true;
				}
			}
			return hasData;
		}
		return true;
	}

	queryDefaultValue(filterInputConfig: IMegaMenuFilterProps) {
		// TODO: update based on filter
		const { defaultValue } = filterInputConfig;
		if (defaultValue) {
			return this.getQueryValue(defaultValue);
		}
	}

	@computed
	get selectedItemCount(): string | undefined {
		let count = 0;
		if (this.value.size > 0) {
			this.value.forEach((menu) => (count += menu.size));
		}
		if (count) {
			const selectedCountLabel =
				this.getQueryValue(this.options.selectedItemsLabelQuery) ||
				this.options.selectedItemsLabel ||
				'{count} Selected';
			return this.translate(selectedCountLabel, { count });
		}
		return;
	}

	get buttonId(): string {
		return `mega-menu-button-${this.options.key}-${this.guid}`;
	}

	get menuId(): string {
		return `mega-menu-menu-${this.options.key}-${this.guid}`;
	}

	//#region MENU ITEM COUNTS
	menuItemCountTemplate(FilterInputConfig: IMegaMenuFilterProps) {
		const defaultTemplate = this.canDisplayMenuItemTotalCount(FilterInputConfig)
			? `({count}/{total})`
			: `({count})`;
		return this.features.menuItemCount?.template || defaultTemplate;
	}

	canDisplayMenuItemCount(filterInputConfig: IMegaMenuFilterProps) {
		if (filterInputConfig) {
			if (filterInputConfig.displayCount !== undefined) {
				return filterInputConfig.displayCount;
			}
			if (this.features.menuItemCount?.enabled !== undefined) {
				return this.features.menuItemCount?.enabled;
			}
		}
		return false;
	}

	canDisplayMenuItemTotalCount(filterInputConfig: IMegaMenuFilterProps) {
		if (filterInputConfig.displayTotalCounts !== undefined) {
			return filterInputConfig.displayTotalCounts;
		}
		if (this.features.menuItemCount?.showTotal !== undefined) {
			return this.features.menuItemCount?.showTotal;
		}
		return false;
	}
	//#endregion

	//#region BUTTON
	get buttonLabel(): string {
		return this.getQueryValue(this.options.labelQuery) || this.options.label || 'Mega Menu Label';
	}

	get buttonPlaceholder(): string {
		return this.getQueryValue(this.options.placeholderQuery) || this.options.placeholder || 'Select an item';
	}

	get buttonCollapsedProps(): TButtonIconProps {
		return this.options.componentProps?.collapsedButtonProps || {};
	}

	get buttonExpandedProps(): TButtonIconProps {
		return this.options.componentProps?.expandedButtonProps || {};
	}
	//#endregion

	//#region PILL

	get hasSelections(): boolean {
		return this.value.size > 0;
	}

	get showPill(): boolean {
		return this.hasSelections && this.features.overallCount?.enabled;
	}

	get pillProps(): IPillProps {
		if (!this.showPill) {
			return {};
		}
		const { pillProps, template, showTotal = false } = this.features.overallCount;

		// Pill Placeholder Values
		let count = 0;
		if (this.value.size > 0) {
			this.value.forEach((menu) => (count += menu.size));
		}
		const total = this.filterItemCount.size;
		const placeholderValues = showTotal ? { count, total } : { count };

		// Pill Label Template
		const props = pillProps || {};
		const defaultValue = showTotal ? '{count}/{total}' : '{count}';
		const pillValue = template || props.value || defaultValue;

		return {
			...props,
			value: this.translate(pillValue, placeholderValues),
		};
	}

	//#endregion

	//#region CLEAR BUTTONS

	get showClearAllButton(): boolean {
		return this.hasSelections && this.features.clearAllButton?.enabled;
	}

	get clearAllButtonProps(): IButtonProps {
		const buttonProps = this.features.clearAllButton.buttonProps || {
			iconProps: {
				asset: 'base.dialog.close',
			},
		};
		return {
			...buttonProps,
			onClick: this.clearAll,
		};
	}

	showMenuListClearButton(filterInputConfig: IMegaMenuFilterProps): boolean {
		return filterInputConfig.displayClearButton || this.features.clearMenuListButton?.enabled || false;
	}

	getMenuListClearButtonProps = (menuKey: string): IButtonProps => {
		const buttonProps = this.features.clearMenuListButton?.buttonProps || {
			text: 'Clear',
		};
		return {
			...buttonProps,
			onClick: () => {
				this.clearValues(menuKey);
			},
		};
	};

	//#endregion

	//#region ITEMS
	@action
	async initializeItems(): Promise<void> {
		await this.orchestrateFilterMenus();
		this.initializeValue();
	}

	@action
	orchestrateFilterMenus = async (): Promise<void> => {
		const { filters } = this.options;
		const filterMappings: Map<string, IFilterMenu> = new Map();
		if (filters) {
			for (let i = 0; i < filters.length; i++) {
				const filter = filters[i];
				const filterMenu = await this.orchestrateFilterMenu(filter);
				if (filterMenu) {
					filterMappings.set(filter.key, filterMenu);
				}
			}
		}
		this.filterMappings = filterMappings;
	};

	orchestrateFilterMenu = async (filterInputConfig: IMegaMenuFilterProps): Promise<IFilterMenu | undefined> => {
		let { useFilterMapping = false } = this.options;
		const { clientConfigurationFilterMapping } = this.filterPropertyStore;

		// Since we are not passing in a key, we will just get the response back
		const filterPropertyValues = this.filterPropertyStore.getFilterPropertyValues(filterInputConfig);
		// If no filter values found, then there is no data to display for the component
		if (filterPropertyValues) {
			const {
				label,
				description,
				isDescriptionHTML = false,
				useFilterMapping: useFilterMappingOverride,
			} = filterInputConfig;
			const filterMappingKey = this.getMenuKey(filterInputConfig);
			if (useFilterMappingOverride !== undefined) {
				useFilterMapping = useFilterMappingOverride;
			}
			const filterMenu: IFilterMenu = {
				key: this.getMenuInputKey(filterInputConfig),
				label: this.translate(label ? this.getQueryValue(label) : undefined),
				description: description ? this.getQueryValue(description) : undefined,
				isDescriptionHTML: isDescriptionHTML,
				isMultiSelect: this.isMultiSelect(filterInputConfig),
				displayClearButton: this.showMenuListClearButton(filterInputConfig),
				options: [],
			};
			// If the filterStore has clientConfigurations populated and the component should display nested filters
			const inputFilterMapping = clientConfigurationFilterMapping.get(filterMappingKey);

			if (useFilterMapping && inputFilterMapping) {
				// We need to compare what properties are configured in the client config map vs what was retrieved from the api
				// We need to display the properties that are configured and ignore the extra values, but if the config value
				// is not in the response then we should not display it

				// the presets build targets es5 and is only happy with map.forEach() which cant be used due to the async get filters call
				// that we need to await
				const mappingKeys = Array.from(inputFilterMapping.keys());
				for (let index = 0; index < mappingKeys.length; index++) {
					const configFilterValue = mappingKeys[index];
					const config = inputFilterMapping.get(configFilterValue);
					if (config) {
						const filterPropertyValue = filterPropertyValues.find(
							(filterValue) => filterValue.label === configFilterValue,
						);
						if (filterPropertyValue) {
							const filterMappingItem: IFilterMenuOption = await this.buildFilterMapping(
								filterInputConfig,
								filterPropertyValue,
							);
							filterMenu.options.push(filterMappingItem as any);
						}
					}
				}
			} else {
				// Here we are not concerned of the config, so just display what we got from the api
				// filterPropertyValues is not a Map() so we do not need to cater for filterPropertyValues
				for (const filterPropertyValue of filterPropertyValues) {
					const filterMappingItem: IFilterMenuOption = await this.buildFilterMapping(
						filterInputConfig,
						filterPropertyValue,
					);

					filterMenu.options.push(filterMappingItem as any);
				}
			}
			return filterMenu;
		}
		return;
	};

	async buildFilterMapping(
		filterInputConfig: IMegaMenuFilterProps,
		filterPropertyValue: IFilterPropertyValueSelection,
	): Promise<IFilterMenuOption> {
		const itemKey = this.getMenuOptionKey(filterInputConfig, filterPropertyValue.label);
		const filterMappingItem: IFilterMenuOption = {
			key: itemKey,
			label: filterPropertyValue.label,
			value: filterPropertyValue.value,
			visible: true,
			checked: filterPropertyValue.isSelected && filterPropertyValue.selectionState === 'full',
			halfChecked: filterPropertyValue.selectionState === 'half',
			property: filterInputConfig.property,
		};

		if (this.canDisplayMenuItemCount(filterInputConfig)) {
			this.filterItemCount.set(itemKey, filterPropertyValue.count || 0);
			this.updateFilterMappingItemCount(filterInputConfig, filterMappingItem, filterPropertyValue);
		}

		return filterMappingItem;
	}

	updateFilterMappingItemCount = (
		filterInputConfig: IMegaMenuFilterProps,
		filterMappingItem: IFilterMenuOption,
		filterValue: IFilterLabelValue,
	) => {
		if (this.canDisplayMenuItemCount(filterInputConfig)) {
			const { count } = filterValue;
			const originalCount = this.filterItemCount.get(filterMappingItem.key) || 0;
			const template = this.menuItemCountTemplate(filterInputConfig);
			const placeholderValues = {
				count,
				total: this.canDisplayMenuItemTotalCount(filterInputConfig) ? originalCount : '',
			};
			filterMappingItem.count = common.commonUtils.replacePlaceholders({
				text: template,
				values: placeholderValues,
			});
		}
	};

	isMultiSelect(filterInputConfig: IMegaMenuFilterProps): boolean {
		const { selectionType = 'multi' } = filterInputConfig;
		return selectionType === 'multi';
	}

	getMenuInputKey(filterInputConfig: IMegaMenuFilterProps): string {
		return filterInputConfig.key;
	}

	getMenuKey(filterInputConfig: IMegaMenuFilterProps): string {
		const entityTypeKey = common.commonUtils.paramCase(filterInputConfig.entityType);
		const propertyKey = common.commonUtils.paramCase(filterInputConfig.property);
		return `${entityTypeKey}|${propertyKey}`;
	}

	getMenuOptionKey = (filterInputConfig: IMegaMenuFilterProps, label: string) => {
		const optionLabelKey = common.commonUtils.paramCase(label);
		return `${this.getMenuKey(filterInputConfig)}|${optionLabelKey}`;
	};
	//#endregion

	//#region SET VALUE
	toggleValue = (menuKey: string, value: IMenuValue) => {
		const filterConfig = this.options.filters.find((filter) => filter.key === menuKey);
		this.filterPropertyStore.toggleFilterValueSelection(filterConfig, value as IFilterPropertyValue);
	};

	@action
	setValues(menuKey: string, values?: Map<string, IMenuValue>) {
		if (values && values.size > 0) {
			this.setFilterStoreValues(menuKey, values);
		} else {
			this.setFilterStoreValues(menuKey, new Map());
		}
	}

	@action
	clearValues = (menuKey: string) => {
		const filter = this.options.filters?.find((filter) => filter.key === menuKey);
		this.filterPropertyStore.clearFilterValueSelection(filter);
	};

	@action
	clearAll = () => {
		this.value.forEach((menuValues, menuKey) => {
			this.clearValues(menuKey);
		});
	};

	setFilterStoreValues = (menuKey: string, values: Map<string, IMenuValue>) => {
		const filter = this.options.filters?.find((filter) => filter.key === menuKey);
		const filterItems: IFilterPropertyValue[] = [];
		values.forEach((item) => filterItems.push(item as IFilterPropertyValue));
		this.filterPropertyStore.updateFilterValueSelections(filter, filterItems);
	};

	sanitizeValue = (value?: Map<string, IMenuValue>): TMenuValue => {
		const { simplifyValue = false } = this.options;
		const sanitizedValue = value || new Map();
		if (simplifyValue) {
			const keys: string[] = [];
			sanitizedValue.forEach((value) => keys.push(value.key));
			return keys;
		}
		const items: IMenuValue[] = [];
		sanitizedValue.forEach((value) => {
			items.push({
				key: value.key,
				label: value.label,
				value: value.value,
			});
		});
		return items;
	};

	// This will not be called if the component is not configured to display counts
	getUpdatedCounts = async () => {
		const { filterValues } = this.filterPropertyStore;
		const workingMappings = new Map(this.filterMappings);
		workingMappings.forEach((menu, menuKey) => {
			const filterConfig = this.options.filters?.find((item) => item.key === menuKey);
			if (!filterConfig || !filterValues) {
				return;
			}

			const filterPropertyValues = this.filterPropertyStore.getFilterPropertyValues(filterConfig);
			for (const filterMappingItem of menu.options) {
				// If the filter value does not exist then the count should be zero'ed
				const filterValueMatch = filterPropertyValues.find(
					(filterValue) => filterValue.label === filterMappingItem.label,
				) || { label: '', value: '', count: 0 };
				this.updateFilterMappingItemCount(filterConfig, filterMappingItem, filterValueMatch);
			}
		});
		runInAction(() => (this.filterMappings = workingMappings));
	};

	@action
	updateValue = async (): Promise<void> => {
		const globalInputsStore = this.storeContext.get<GlobalInputsStore>('globalInputsStore');
		if (!this.options.filters) {
			return;
		}

		let usingFilters = false;
		const updatedValue = new Map<string, Map<string, IMenuValue>>();
		const globalInputValue: { [key: string]: TMenuValue } = {};

		this.options.filters?.forEach((filter) => {
			const { key: menuKey } = filter;
			const filterPropertyValues = this.filterPropertyStore.getFilterPropertyValues(filter);
			const menuValues = new Map<string, IMenuValue>();
			const menu = this.filterMappings.get(menuKey);
			for (const filterMappingItem of menu.options) {
				const filterValueMatch = filterPropertyValues.find(
					(filterValue) => filterValue.label === filterMappingItem.label,
				);
				filterMappingItem.checked = filterValueMatch?.isSelected;
				if (filterValueMatch && filterValueMatch.isSelected) {
					menuValues.set(filterMappingItem.key, filterMappingItem);
				}
			}
			updatedValue.set(menuKey, menuValues);
			globalInputValue[menuKey] = this.sanitizeValue(menuValues);
			usingFilters = true;
		});
		this.value = updatedValue;
		globalInputsStore.setValue(this.inputKey, globalInputValue, !usingFilters);
		this.getUpdatedCounts();
	};

	getPropertyFromKey = (key: string) => {
		const [entityType, property] = key.split('|');
		return property;
	};
	//#endregion

	//#region GET VALUE
	@action
	initializeValue(): void {
		const appStore = this.storeContext.get<AppStore>('appStore');
		const globalInputsStore = this.storeContext.get<GlobalInputsStore>('globalInputsStore');

		this.options.filters?.forEach((filter) => {
			const { key: menuKey } = filter;
			let menuValues: Map<string, IMenuValue> | undefined;
			if (appStore.loadInitialValuesFromStorage) {
				const valueFromStorage = globalInputsStore.getValue(menuKey);
				if (valueFromStorage) {
					menuValues = this.initializeValueForComponent(menuKey, valueFromStorage);
				}
			}
			if (!menuValues) {
				const defaultValue = this.queryDefaultValue(filter);
				if (defaultValue) {
					const selectedItems = this.isMultiSelect(filter)
						? this.parseMultiSelectDefaults(menuKey, defaultValue)
						: this.parseSelectDefault(menuKey, defaultValue);
					menuValues = this.initializeValueForComponent(menuKey, selectedItems);
				}
			}
			this.setValues(menuKey, menuValues);
		});
	}

	initializeValueForComponent = (menuKey: string, value?: TMenuValue): Map<string, IMenuValue> | undefined => {
		// Save the resolved value in this.value
		if (value) {
			const menuValues: Map<string, IMenuValue> = new Map();
			if (Array.isArray(value)) {
				for (const v of value) {
					const menuValueItem = this.getValueAsMenuValue(menuKey, v);
					if (menuValueItem) {
						menuValues.set(menuValueItem.key, menuValueItem);
					}
				}
			} else {
				const menuValueItem = this.getValueAsMenuValue(menuKey, value);
				if (menuValueItem) {
					menuValues.set(menuValueItem.key, menuValueItem);
				}
			}
			return menuValues;
		}
		return;
	};

	getValueAsMenuValue = (menuKey: string, value: string | IMenuValue): IMenuValue | undefined => {
		if (typeof value === 'string') {
			// Find the filterMapping item based on the key then pop the value and label for the item
			const matchingItem = this.findMatchingItem(menuKey, value);
			if (matchingItem) {
				const { key, label, value } = matchingItem;
				return {
					key,
					label,
					value,
				};
			}
		} else {
			return value;
		}
	};

	findMatchingItem = (menuKey: string, defaultValue: string): IFilterMenuOption | undefined => {
		if (this.filterMappings.has(menuKey)) {
			const menu = this.filterMappings.get(menuKey);
			if (menu.options.length > 0) {
				return menu.options.find((optionItem) => {
					if (optionItem.key === defaultValue) {
						return true;
					}
				});
			}
		}
	};

	parseSelectDefault = (menuKey: string, rawDefault: string | IMenuValue): IMenuValue | undefined => {
		const defaultValue = this.getItemValue(rawDefault);
		const matchingItem = this.findMatchingItem(menuKey, defaultValue);
		this.storeContext
			.get<AppStore>('appStore')
			.logDebug('Menu Store > parse select default', { defaultValue, matchingItem });
		if (matchingItem) {
			return matchingItem;
		}
		this.logMissingDefaultWarning([defaultValue]);
	};

	parseMultiSelectDefaults = (menuKey: string, rawDefaults: TMenuValue): IMenuValue[] => {
		const defaultItems = [...(Array.isArray(rawDefaults) ? rawDefaults : [rawDefaults])];
		const missingValues: string[] = [];
		const matchingItems: IMenuValue[] = [];
		for (const defaultItem of defaultItems) {
			const defaultValue = this.getItemValue(defaultItem);
			const matchingItem = this.findMatchingItem(menuKey, defaultValue);
			this.storeContext
				.get<AppStore>('appStore')
				.logDebug('Menu Store > parse multi-select default', { defaultValue, matchingItem });
			if (matchingItem) {
				matchingItems.push(matchingItem);
			} else {
				missingValues.push(defaultValue);
			}
		}
		this.logMissingDefaultWarning(missingValues);
		return matchingItems;
	};

	// Gets the item key, as it is the identifier of the filter mapping items
	getItemValue = (item: string | IMenuValue): string => {
		return typeof item === 'string' ? item : item.key;
	};

	logMissingDefaultWarning = (missingValues: string[]) => {
		// Displays a warning message for all the items that should be defaulted but do not exist in the dropdown
		if (missingValues.length !== 0) {
			const warningMessage = `Unable to apply default value of ${missingValues.join(
				',',
			)} to the input dropdown for '${this.inputKey}' as the value(s) doesn't exist in the available list`;
			console.warn(warningMessage);
		}
	};
	//#endregion
}
